import { useState } from "react";
import clsx from "clsx";
import type { HeroClass, SkillName } from "../types";
import { getSkillIconPath } from "../utils/assets";

const CLASS_STYLE: Record<HeroClass, { accent: string; glow: string; bg: string }> = {
  ARCHER: { accent: "#c084fc", glow: "#e9d5ff", bg: "#1a0f2e" },
  WARRIOR: { accent: "#f87171", glow: "#fecaca", bg: "#1f0f0f" },
  MAGE: { accent: "#60a5fa", glow: "#bae6fd", bg: "#0f1729" },
};

type GlyphProps = { skill: string; heroClass: HeroClass; accent: string; glow: string };

function ArcherGlyph({ skill, accent, glow }: Omit<GlyphProps, "heroClass">) {
  switch (skill) {
    case "SKILL_1":
    case "BASIC":
      return (
        <>
          <path d="M32 12 L38 44 L32 40 L26 44 Z" fill={accent} />
          <line x1="32" y1="40" x2="32" y2="52" stroke={glow} strokeWidth="2.5" strokeLinecap="round" />
        </>
      );
    case "SKILL_2":
      return (
        <>
          <path d="M24 14 L28 44 L24 40 L20 44 Z" fill={accent} opacity="0.9" />
          <path d="M40 14 L44 44 L40 40 L36 44 Z" fill={glow} />
        </>
      );
    case "ULT":
      return (
        <polygon
          points="32,10 37,24 52,24 40,33 44,48 32,39 20,48 24,33 12,24 27,24"
          fill={accent}
          stroke={glow}
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
      );
    case "SKILL_4":
      return (
        <>
          <path d="M18 38 L22 18 L26 38 Z" fill={accent} opacity="0.75" />
          <path d="M30 40 L32 14 L34 40 Z" fill={glow} />
          <path d="M38 38 L42 18 L46 38 Z" fill={accent} opacity="0.75" />
        </>
      );
    case "SKILL_5":
      return (
        <>
          <path d="M32 46 C24 38 20 32 20 26 C20 20 25 16 32 16 C39 16 44 20 44 26 C44 32 40 38 32 46 Z" fill={accent} stroke={glow} strokeWidth="1.5" />
          <path d="M28 28 L36 28 M32 24 L32 32" stroke={glow} strokeWidth="2" strokeLinecap="round" />
        </>
      );
    case "SKILL_6":
      return (
        <>
          <circle cx="32" cy="32" r="14" fill="none" stroke={accent} strokeWidth="2.5" />
          <circle cx="32" cy="32" r="5" fill={glow} />
          <path d="M32 12 L32 20 M32 44 L32 52 M14 32 L22 32 M42 32 L50 32" stroke={accent} strokeWidth="2" strokeLinecap="round" />
        </>
      );
    case "SKILL_7":
      return (
        <>
          <path d="M32 14 C26 24 22 30 22 36 C22 42 26 46 32 46 C38 46 42 42 42 36 C42 30 38 24 32 14 Z" fill={accent} />
          <ellipse cx="32" cy="50" rx="6" ry="3" fill={glow} opacity="0.7" />
        </>
      );
    case "SKILL_8":
      return (
        <>
          <path d="M32 10 L42 32 L32 28 L22 32 Z" fill={glow} />
          <path d="M32 28 L38 54 L32 50 L26 54 Z" fill={accent} />
        </>
      );
    default:
      return null;
  }
}

function WarriorGlyph({ skill, accent, glow }: Omit<GlyphProps, "heroClass">) {
  switch (skill) {
    case "SKILL_1":
    case "BASIC":
      return (
        <>
          <path d="M20 48 L44 16" stroke={accent} strokeWidth="4" strokeLinecap="round" />
          <path d="M38 16 L44 16 L44 22" stroke={glow} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
        </>
      );
    case "SKILL_2":
      return (
        <>
          <path d="M18 46 L34 20" stroke={accent} strokeWidth="3.5" strokeLinecap="round" />
          <path d="M30 46 L46 20" stroke={glow} strokeWidth="3.5" strokeLinecap="round" />
        </>
      );
    case "ULT":
      return (
        <polygon points="32,12 40,28 56,28 44,38 48,54 32,44 16,54 20,38 8,28 24,28" fill={accent} stroke={glow} strokeWidth="1.5" />
      );
    case "SKILL_4":
      return (
        <>
          <circle cx="32" cy="32" r="16" fill="none" stroke={accent} strokeWidth="2" />
          <path d="M16 32 L48 32 M24 20 L40 44 M40 20 L24 44" stroke={glow} strokeWidth="2.5" strokeLinecap="round" />
        </>
      );
    case "SKILL_5":
      return (
        <>
          <path d="M20 40 L20 24 C20 18 26 14 32 14 C38 14 44 18 44 24 L44 40 Z" fill="none" stroke={accent} strokeWidth="2.5" />
          <path d="M32 22 L32 34" stroke={glow} strokeWidth="2" strokeLinecap="round" />
        </>
      );
    case "SKILL_6":
      return (
        <>
          <path d="M20 48 L44 16" stroke={accent} strokeWidth="4" strokeLinecap="round" />
          <path d="M14 38 L20 44" stroke={glow} strokeWidth="2" strokeLinecap="round" />
          <path d="M50 26 L44 20" stroke={glow} strokeWidth="2" strokeLinecap="round" />
        </>
      );
    case "SKILL_7":
      return (
        <>
          <polygon points="32,14 42,26 38,44 26,44 22,26" fill={accent} stroke={glow} strokeWidth="1.5" />
          <circle cx="32" cy="30" r="4" fill={glow} />
        </>
      );
    case "SKILL_8":
      return (
        <>
          <circle cx="32" cy="32" r="18" fill="none" stroke={accent} strokeWidth="2" />
          <circle cx="32" cy="32" r="12" fill="none" stroke={glow} strokeWidth="1.5" opacity="0.8" />
          <circle cx="32" cy="32" r="4" fill={glow} />
        </>
      );
    default:
      return null;
  }
}

function MageGlyph({ skill, accent, glow }: Omit<GlyphProps, "heroClass">) {
  switch (skill) {
    case "SKILL_1":
    case "BASIC":
      return (
        <>
          <path d="M32 10 L22 28 L30 28 L18 54 L34 32 L26 32 Z" fill={accent} stroke={glow} strokeWidth="1" strokeLinejoin="round" />
        </>
      );
    case "SKILL_2":
      return (
        <>
          <circle cx="24" cy="28" r="6" fill={accent} opacity="0.8" />
          <circle cx="40" cy="28" r="6" fill={glow} />
          <circle cx="32" cy="42" r="6" fill={accent} opacity="0.6" />
        </>
      );
    case "ULT":
      return (
        <>
          <circle cx="32" cy="32" r="18" fill="none" stroke={accent} strokeWidth="2" />
          <path d="M32 14 L32 50 M18 32 L46 32 M22 22 L42 42 M42 22 L22 42" stroke={glow} strokeWidth="2" strokeLinecap="round" />
        </>
      );
    case "SKILL_4":
      return (
        <>
          <circle cx="32" cy="32" r="8" fill={accent} opacity="0.5" />
          <path d="M32 16 L32 24 M32 40 L32 48 M16 32 L24 32 M40 32 L48 32" stroke={glow} strokeWidth="2.5" strokeLinecap="round" />
        </>
      );
    case "SKILL_5":
      return (
        <>
          <path d="M32 14 L38 30 L54 34 L42 44 L44 60 L32 52 L20 60 L22 44 L10 34 L26 30 Z" fill={accent} stroke={glow} strokeWidth="1" />
        </>
      );
    case "SKILL_6":
      return (
        <>
          <circle cx="22" cy="26" r="5" fill={accent} />
          <circle cx="42" cy="26" r="5" fill={accent} />
          <circle cx="32" cy="42" r="5" fill={glow} />
          <path d="M16 48 L48 48" stroke={accent} strokeWidth="2" strokeLinecap="round" />
        </>
      );
    case "SKILL_7":
      return (
        <>
          <path d="M20 40 C20 28 26 18 32 18 C38 18 44 28 44 40 Z" fill={accent} opacity="0.7" />
          <path d="M26 40 L38 40 M32 34 L32 46" stroke={glow} strokeWidth="2.5" strokeLinecap="round" />
        </>
      );
    case "SKILL_8":
      return (
        <>
          <circle cx="32" cy="32" r="16" fill="none" stroke={accent} strokeWidth="2" />
          <path d="M20 32 L44 32 M32 20 L32 44" stroke={glow} strokeWidth="2" />
          <circle cx="32" cy="32" r="4" fill={glow} />
        </>
      );
    default:
      return null;
  }
}

function Glyph({ skill, heroClass, accent, glow }: GlyphProps) {
  const inner =
    heroClass === "WARRIOR"
      ? WarriorGlyph({ skill, accent, glow })
      : heroClass === "MAGE"
        ? MageGlyph({ skill, accent, glow })
        : ArcherGlyph({ skill, accent, glow });

  if (inner) return inner;
  return <text x="32" y="36" textAnchor="middle" fill={glow} fontSize="11" fontWeight="bold">?</text>;
}

interface SkillIconProps {
  heroClass: HeroClass;
  skill: SkillName | string;
  className?: string;
  alt?: string;
}

export function SkillIcon({ heroClass, skill, className, alt }: SkillIconProps) {
  const [glyphFallback, setGlyphFallback] = useState(false);
  const style = CLASS_STYLE[heroClass];

  if (!glyphFallback) {
    return (
      <img
        src={getSkillIconPath(heroClass, skill)}
        alt={alt ?? skill}
        className={clsx("rounded-lg border border-kingdom-mid shrink-0 object-cover bg-kingdom-dark", className)}
        onError={() => setGlyphFallback(true)}
      />
    );
  }

  return (
    <svg
      viewBox="0 0 64 64"
      className={clsx("rounded-lg border border-kingdom-mid shrink-0", className)}
      role="img"
      aria-label={alt ?? skill}
    >
      <rect width="64" height="64" rx="10" fill={style.bg} />
      <rect x="2" y="2" width="60" height="60" rx="8" fill="none" stroke={style.accent} strokeWidth="1.5" opacity="0.55" />
      <Glyph skill={skill} heroClass={heroClass} accent={style.accent} glow={style.glow} />
    </svg>
  );
}
